/**
 * Eligible broker accounts for an instrument — which accounts can trade it
 * (segment enabled + account enabled), plus margin-estimate context.
 * Backed by GET /admin/instruments/{id}/eligible-accounts. Per-account funds
 * are read separately via getFunds (independent degrade).
 */
import { z } from 'zod';

import type { ApiResult } from './client';
import { get } from './client';

export const eligibleAccountSchema = z.object({
  broker_account_id: z.string(),
  display_name: z.string().nullable(),
  broker_type: z.string(),
  client_id: z.string(),
  // Manual-arm state: true = usable for manual orders now (or a paper account).
  // Tolerant default so an older backend response doesn't hard-fail the parse.
  armed: z.boolean().default(false),
});
export const eligibleAccountsSchema = z.object({
  instrument_id: z.string(),
  symbol: z.string(),
  // QUOTE currency of the instrument (ISO code). The margin estimate is priced
  // off this instrument, so it is what the estimate is denominated in — NOT the
  // account's settlement currency, which the per-account funds read carries.
  // Required on purpose: an optional field would just grow a default again.
  currency: z.string(),
  segment_id: z.string().nullable(),
  asset_class: z.string().nullable(),
  lot_size: z.number(),
  tick_size: z.number(),
  contract_multiplier: z.number(),
  margin_factors: z.record(z.number()),
  strike: z.number().nullable().optional(),
  accounts: z.array(eligibleAccountSchema),
});
export type EligibleAccount = z.infer<typeof eligibleAccountSchema>;
export type EligibleAccounts = z.infer<typeof eligibleAccountsSchema>;

export const getEligibleAccounts = (
  instrumentId: string,
  signal?: AbortSignal,
): Promise<ApiResult<EligibleAccounts>> =>
  get(
    `/admin/instruments/${encodeURIComponent(instrumentId)}/eligible-accounts`,
    eligibleAccountsSchema,
    { signal },
  ) as Promise<ApiResult<EligibleAccounts>>;
